import React from "react";
import pluralize from "pluralize";
import { TimePicker } from "./time_picker";
import { setSameDay } from "../lib/datetime";
import { parseTime } from "../lib/datetime";
import { getDurationInHours } from "../lib/datetime";
import {
  format,
  subMinutes,
  addMinutes,
  isAfter,
  differenceInMinutes,
} from "date-fns";
import { Spacing } from "./spacing";
import { Column } from "./column";
import { Text } from "./text";

export interface TimeRangeValue {
  startTime?: Date;
  endTime?: Date;
}

interface TimeRangePickerProps {
  date?: Date;
  value?: TimeRangeValue;
  onChange?: (value: TimeRangeValue) => void;
  /** Minimum duration in minutes. */
  minDuration?: number;
  /** Time in HHmm format */
  minStartTime?: string | ((date: Date) => string);
  /** Time in HHmm format */
  maxEndTime?: string | ((date: Date) => string);
  isStartTimeBlocked?: (time: Date) => boolean;
  isEndTimeBlocked?: (time: Date, selectedStartTime: Date) => boolean;
}

function getEffectiveBoundTime(
  bound: string | ((date: Date) => string),
  date?: Date
): Date {
  if (date) {
    if (typeof bound === "string") {
      return setSameDay(parseTime(bound), date);
    } else {
      return setSameDay(parseTime(bound(date)), date);
    }
  } else {
    if (typeof bound === "string") {
      return parseTime(bound);
    } else {
      return parseTime(bound(new Date()));
    }
  }
}

export function TimeRangePicker(props: TimeRangePickerProps) {
  const {
    onChange = () => {},
    date,
    value = {},
    minDuration = 60,
    maxEndTime = "2300",
    minStartTime = "0600",
    isStartTimeBlocked,
    isEndTimeBlocked,
  } = props;
  const { startTime, endTime } = value;

  if (minDuration < 60) {
    throw Error(
      "TimeRangePicker: [minDuration] should not be less than 60 minutes. Set [minDuration] to be above 60 minutes."
    );
  }

  const effectiveMinStartTime = getEffectiveBoundTime(minStartTime, date);
  const effectiveMaxEndTime = getEffectiveBoundTime(maxEndTime, date);
  const startTimeMaxEndTime = subMinutes(effectiveMaxEndTime, minDuration);

  const handleStartTimeChange = React.useCallback(
    (time: Date) => {
      if (!startTime || !endTime) {
        onChange({
          startTime: time,
          endTime: addMinutes(time, minDuration),
        });

        return;
      }

      const diff = differenceInMinutes(time, startTime);
      const nextEndTime = addMinutes(endTime, diff);

      // if the duration goes beyond the allowed end time, it should reset
      if (isAfter(nextEndTime, effectiveMaxEndTime)) {
        onChange({
          startTime: time,
          endTime: undefined,
        });

        return;
      }

      if (isEndTimeBlocked && isEndTimeBlocked(nextEndTime, time)) {
        onChange({
          startTime: time,
          endTime: undefined,
        });
        return;
      }

      onChange({
        startTime: time,
        endTime: nextEndTime,
      });
    },
    [
      startTime,
      endTime,
      effectiveMaxEndTime,
      minDuration,
      onChange,
      isEndTimeBlocked,
    ]
  );

  const handleEndTimeChange = React.useCallback(
    (time: Date) => {
      onChange({
        startTime,
        endTime: time,
      });
    },
    [startTime, onChange]
  );

  return (
    <Column>
      <Text fontSize="sm" color="muted">
        Start time
      </Text>
      <Spacing height={8} />
      <TimePicker
        date={date}
        value={startTime}
        placeholder="Start time"
        startTime={effectiveMinStartTime}
        endTime={startTimeMaxEndTime}
        onChange={handleStartTimeChange}
        formatter={(time) => format(time, "h:mm a")}
        disabled={!date}
        testID="start-time"
        isBlocked={isStartTimeBlocked}
      />
      <Spacing height={16} />
      <Text fontSize="sm" color="muted">
        End time
      </Text>
      <Spacing height={8} />
      <TimePicker
        date={date}
        value={endTime}
        startTime={startTime ? addMinutes(startTime, minDuration) : undefined}
        endTime={effectiveMaxEndTime}
        placeholder="End time"
        disabled={!startTime}
        testID="end-time"
        formatter={(time) => {
          if (!startTime) {
            throw Error("expected time to exist before selecting duration.");
          }

          const duration = getDurationInHours(startTime, time);

          return `${format(time, "h:mm a")} (${duration} ${pluralize(
            "hour",
            duration
          )})`;
        }}
        onChange={handleEndTimeChange}
        isBlocked={(time) => {
          if (startTime && isEndTimeBlocked) {
            return isEndTimeBlocked(time, startTime);
          }

          return false;
        }}
      />
    </Column>
  );
}
